﻿import Oidc from 'oidc-client';
import { SentrySettingsClient, SentryApplicationSettingsDTO } from "../generated";

export class LogLevels {
    static readonly NONE: number = Oidc.Log.NONE;
    static readonly ERROR: number = Oidc.Log.ERROR;
    static readonly WARN: number = Oidc.Log.WARN;
    static readonly INFO: number = Oidc.Log.INFO;
    static readonly DEBUG: number = Oidc.Log.DEBUG;
}

const loc = window.location;
const host = loc.protocol + "//" + loc.host;

let mgr: Oidc.UserManager;
let loggedInUser: Oidc.User;
let clientId = "";
let readyCallback: (user?: Oidc.User) => void;


function reloadApplication(origin: string) {
    const url = location.protocol + '//' + location.host + location.pathname;
    console.log(origin + ", reloading app (" + url + ")");

    location.replace(url);
}


function getUrlVar(key: string) {
    const result = new RegExp(key + "=([^&]*)", "i").exec(window.location.search);
    return result && result[1] || "";
}



export function getUser(): Oidc.User {
    return loggedInUser;
}

export function getUsername(): string {
    if (!loggedInUser || !loggedInUser.profile || !loggedInUser.profile.name)
        return "";

    return loggedInUser.profile.name;
}

export function getDisplayname(): string {
    if (!loggedInUser || !loggedInUser.profile)
        return "";

    const givenName = loggedInUser.profile.given_name;
    const familyName = loggedInUser.profile.family_name;
    if (givenName !== null || familyName !== null) {
        if (!givenName)
            return familyName ?? "";
        else if (!familyName)
            return givenName;
        else
            return givenName + " " + familyName;
    }

    return getUsername();
}

export function getUserTenant(): string {
    if (!loggedInUser || !loggedInUser.profile || !loggedInUser.profile.tenant)
        return "";

    return loggedInUser.profile.tenant;
}

export function userHasRole(roles: Array<string>, anyRole = true): boolean {
    if (!loggedInUser || !loggedInUser.profile || !loggedInUser.profile.role)
        return false;

    //If there is only one role it is passed as a string make it an array
    //otherwise a check on a substring will be done.
    if (typeof loggedInUser.profile.role === "string")
        loggedInUser.profile.role = [loggedInUser.profile.role];

    if (anyRole) {
        return roles.some(role => {
            return (loggedInUser.profile.role as string[]).indexOf(role) !== -1;
        })
    }
    else {
        return roles.every(role => {
            return (loggedInUser.profile.role as string[]).indexOf(role) !== -1;
        })
    }
}

export function userGetRoles(): string[] {
    if (!loggedInUser || !loggedInUser.profile || !loggedInUser.profile.role)
        return [];

    return loggedInUser.profile.role;
}

export function userLogout(): void {
    document.cookie = `token_${clientId}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    mgr.signoutRedirect();
}

function storePath(): void {
    const queryIndex = loc.href.indexOf('?');
    const query = queryIndex > -1 ? loc.href.slice(queryIndex) : "";
    window.localStorage.setItem("query", query);
    window.localStorage.setItem("hash", loc.hash);
    window.localStorage.setItem("path", loc.pathname);
}

function applyStoredPath(): void {
    const storedQuery = window.localStorage.getItem("query");
    const storedHash = window.localStorage.getItem("hash");
    const storedPath = window.localStorage.getItem("path");
    let path = host + storedPath;
    if (storedQuery) {
        path += storedQuery;
    } else if (storedHash) {
        path += storedHash;
    }
    window.history.pushState({ path: path }, '', path);
}

function getFullUri(path: string | undefined) {
    if (!path)
        return "";

    return host + (path.startsWith("/") ? path : `/${path}`);
}


function initUserManager(settings: SentryApplicationSettingsDTO) {

    const token = getUrlVar("token");
    let tokenAcr = "";
    if (token != null && token != "")
        tokenAcr = "token:" + token;

    let redirectUri = getFullUri(settings.redirectUri);
    if (redirectUri === "")
        console.error("redirectUri not valid");

    let silentRenewUri = getFullUri(settings.silentRenewUri);
    if (silentRenewUri === "")
        console.error("silentRenewUri not valid");

    let postLogoutUri = getFullUri(settings.postLogoutUri);
    if (postLogoutUri === "")
        console.error("postLogoutUri not valid");

    mgr = new Oidc.UserManager({
        authority: settings.uri,
        client_id: settings.clientId,
        includeIdTokenInSilentRenew: false,
        redirect_uri: redirectUri,
        post_logout_redirect_uri: postLogoutUri,

        // these two will be done dynamically from the buttons clicked, but are
        // needed if you want to use the silent_renew
        response_type: "id_token token",
        scope: settings.requestedScopes ? settings.requestedScopes.join(" ") : "",

        // silent renew will get a new access_token via an iframe
        // just prior to the old access_token expiring (60 seconds prior)
        silent_redirect_uri: silentRenewUri,
        automaticSilentRenew: true,

        // will raise events for when user has performed a logout at IdentityServer
        monitorSession: true,

        // this will allow all the OIDC protocol claims to vbe visible in the window. normally a client app
        // wouldn't care about them or want them taking up space
        filterProtocolClaims: true,

        // this will use the user info endpoint if it's an OIDC request and there's an access_token
        loadUserInfo: true,

        acr_values: tokenAcr
    });

    mgr.events.addUserUnloaded(() => reloadApplication("User Unloaded"));
    mgr.events.addUserSignedOut(() => reloadApplication("User Signed Out"));
    mgr.events.addAccessTokenExpired(() => reloadApplication("Access Token Expired"));
    mgr.events.addSilentRenewError((error) => reloadApplication("Silent Renew Error: " +error));
    mgr.events.addUserLoaded((u: Oidc.User) => {
        document.cookie = `token_${clientId}=${u.access_token}; path=/; max-age=${u.expires_in}`;

        loggedInUser = u;
    });
}


export async function start(callback: (user?: Oidc.User) => void, logLevel = LogLevels.WARN) {
    Oidc.Log.logger = console;
    Oidc.Log.level = logLevel;

    const sentrySettingsClient = new SentrySettingsClient();
    let settings = await sentrySettingsClient.get();

    if (settings == null) {
        console.error("Error getting settings");
        return;
    }
    
    clientId = settings.clientId;
    readyCallback = callback;

    initUserManager(settings);

    // checks to see if the page being loaded looks like a login callback
    if (loc.hash.indexOf("#id_token") === -1) {
        storePath();
        mgr.clearStaleState().then(() => {
            mgr.signinRedirect({
                scope: settings?.requestedScopes ? settings?.requestedScopes.join(" ") : "",
                response_type: "id_token token"
            });
        });
    } else {
        mgr.signinRedirectCallback().then((user: Oidc.User) => {
            applyStoredPath();

            loggedInUser = user

            //loggedin user
            if (readyCallback)
                readyCallback(user);
            else
                console.error("readyCallback null")

        }, (error: any) => {
            console.error(error);
            mgr.signinRedirect({
                scope: settings?.requestedScopes ? settings?.requestedScopes.join(" ") : "",
                response_type: "id_token token"
            });
        });
    }
}